/*
 * Copyright (c) 2019. Nedaware SL
 */

import * as moment from 'moment';
import {LoginAnswerInterface} from '../login-interface';
import * as jwtDecode from 'jwt-decode';
import {ApiInterface} from '../api-interface';

/**
 * A token to manage the sessions in the system, it can be valid
 * or not.
 */
export class Token {
    /** The key for the token in local storage */
    private static readonly LS_TOKEN_KEY = 'token_token';

    private readonly _token: string;
    private readonly _expiration: moment.Moment;
    private readonly _roles: string[];
    private readonly _username: string;
    private readonly _iat: moment.Moment;

    private constructor(token: string) {
        this._token = token;
        try {
            const obj = jwtDecode.default(token);
            this._expiration = moment.unix(obj['exp']);
            this._roles = obj['roles'];
            this._username = obj['username'];
            this._iat = moment.unix(obj['iat']);
        } catch (error) {
            this._expiration = null;
            this._roles = [];
            this._username = null;
            this._iat = null;
        }
    }

    public static invalidToken(): Token {
        return new Token(null);
    }

    public static fromLocalStorage(): Token {

        const token = localStorage.getItem(Token.LS_TOKEN_KEY);
        if (token !== null) {
            return new Token(token);
        } else {
            return Token.invalidToken();
        }
    }

    public static fromLoginAnswer(answer: LoginAnswerInterface): Token {
        return new Token(answer.token);
    }

    /**
     * Elimina el token de localStorage y retorna un token equivalente
     * a la situación resultante (guardar el token resultante).
     */
    static removeFromStorage(): Token {
        localStorage.removeItem(Token.LS_TOKEN_KEY);
        return Token.invalidToken();
    }

    /**
     * Guarda el token en localStorage
     */
    public store() {
        if (this.isValid()) {
            localStorage.setItem(Token.LS_TOKEN_KEY, this.token);
        } else {
            Token.removeFromStorage();
        }
    }

    public isValid() {
        return this.isNotEmpty() && this.isNotExpired() && (this.roles.includes(ApiInterface.ROLES.EMPLEADO) || this.roles.includes(ApiInterface.ROLES.COMERCIAL));
    }

    public isNotExpired() {
        return moment().isBefore(this.expiration);
    }

    public isExpired() {
        return this.expiration && !this.isNotExpired();
    }

    public isNotEmpty() {
        return this.token !== null &&
            this.expiration !== null;
    }

    get token(): string {
        return this._token;
    }

    get expiration(): moment.Moment {
        return this._expiration;
    }

    get roles(): string[] {
        if (this.isNotExpired()) {
            return Object.values(this._roles);
        } else {
            return [];
        }
    }

    get username(): string {
        return this._username;
    }

    get iat(): moment.Moment {
        return this._iat;
    }
}
